import { apiVariables } from './apiVariables';
import { apiClient } from './config';
import { getSensitiveData } from '../../utils/secureStorage';

interface CachedUrl {
  url: string;
  expiresAt: number; 
}

const photoUrlCache = new Map<string, CachedUrl>();
const photoBlobCache = new Map<string | number, string>();

const parseSasExpiry = (url: string): number => {
  try {
    const se = new URL(url).searchParams.get('se');
    if (se) {
      const expiry = new Date(se).getTime();
      if (!Number.isNaN(expiry)) return expiry - 60_000; 
    }
  } catch {
  }
  return Date.now() + 10 * 60 * 1000; 
};

const isUrlCacheValid = (cached: CachedUrl): boolean => Date.now() < cached.expiresAt;

/**
 * @param photoId - The photo ID
 * @returns Promise<string> - The SAS URL for the photo
 */
export const getPhotoUrl = async (photoId: string | number): Promise<string> => {
  if (!photoId) {
    return '';
  }

  const cacheKey = String(photoId);
  const cached = photoUrlCache.get(cacheKey);
  if (cached && isUrlCacheValid(cached)) {
    return cached.url;
  }

  try {
    const token = await getSensitiveData("authToken");
    
    const response = await apiClient.get(
      `${apiVariables.GetPhotoUrlByPhotoId}${photoId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // API returns { success: boolean, data: { attachmentId, fileUrl, fileType } }
    // Handle both nested object format and legacy string format
    const responseData = response?.data?.data || response?.data;
    const photoUrl = responseData?.fileUrl || responseData;

    if (photoUrl && typeof photoUrl === 'string') {
      photoUrlCache.set(cacheKey, { url: photoUrl, expiresAt: parseSasExpiry(photoUrl) });
      return photoUrl;
    }
    
    return '';
  } catch (error) {
    console.error(`Failed to fetch photo URL for photoId ${photoId}:`, error);
    return '';
  }
};

/**
 * @param photoId - The photo ID
 * @returns Promise<string> - The blob URL for the photo
 */
export const getPhotoBlobUrl = async (photoId: string | number): Promise<string> => {
  if (!photoId) {
    return '';
  }

  const cacheKey = String(photoId);
  if (photoBlobCache.has(cacheKey)) {
    return photoBlobCache.get(cacheKey)!;
  }

  try {
    const token = await getSensitiveData("authToken");
    
    const response = await apiClient.get(
      `${apiVariables.GetPhotoSasUrlByPhotoId}${photoId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', 
      }
    );

    const blob = response.data;
    const blobUrl = URL.createObjectURL(blob);

    photoBlobCache.set(cacheKey, blobUrl);
    
    return blobUrl;
  } catch (error) {
    console.error(`Failed to fetch photo blob for photoId ${photoId}:`, error);
    return '';
  }
};
export const getPhotoSasUrl = getPhotoUrl;

export const invalidatePhotoUrl = (photoId: string | number): void => {
  photoUrlCache.delete(String(photoId));
};

export const peekCachedPhotoUrl = (photoId: string | number | null | undefined): string => {
  if (!photoId) return '';
  const cached = photoUrlCache.get(String(photoId));
  return cached && isUrlCacheValid(cached) ? cached.url : '';
};

export const clearPhotoUrlCache = () => {
  photoBlobCache.forEach((url) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
  photoUrlCache.clear();
  photoBlobCache.clear();
};

/**
 * @param photoIds - Array of photo IDs to preload
 */
export const preloadPhotoUrls = async (photoIds: (string | number)[]): Promise<void> => {
  const promises = photoIds
    .filter(id => {
      if (!id) return false;
      const cached = photoUrlCache.get(String(id));
      return !cached || !isUrlCacheValid(cached);
    })
    .map(id => getPhotoUrl(id));
  
  await Promise.allSettled(promises);
};

/**
 * @param photoIds 
 */
export const preloadPhotoBlobUrls = async (photoIds: (string | number)[]): Promise<void> => {
  const promises = photoIds
    .filter(id => id && !photoBlobCache.has(String(id)))
    .map(id => getPhotoBlobUrl(id));
  
  await Promise.allSettled(promises);
};
